// src/components/BookCover/BookCoverDesigner.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as fabric from 'fabric';
import JsBarcode from 'jsbarcode';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import TextStyleToolbar from './TextStyleToolbar';
import BookCover3D from './BookCover3D';
import CollaboratePanel from './CollaboratePanel';
import CollaboratorBar from './CollaboratorBar';
import CanvasContextMenu from './CanvasContextMenu';
import CoverRightSidebar from './CoverRightSidebar';
import CheckpointTimelineModal from './CheckpointTimelineModal';
import { API_BASE } from '../../utils/api';
import useCanvasSetup from '../../hooks/useCanvasSetup';
import useCanvasAutosave from '../../hooks/useCanvasAutosave';
import useCommentBadges from '../../hooks/useCommentBadges';
import { useToast } from '../../context/ToastContext';
import usePdfGenerator from './usePdfGenerator';
import PdfPreviewModal from './PdfPreviewModal';
import { useRuler, RULER_SIZE } from '../../hooks/useRuler';
import '../../styles/BookCoverDesigner.css';

// All sizes at exactly 66 px/inch so ruler conversion is: canvasX / 66 = inches.
// realW/realH (inches) are stored here for the future ruler implementation.
const BOOK_SIZE_DIMS = {
  half_letter: { w: 363, h: 561, realW: 5.5,   realH: 8.5   },
  trade_6x9:   { w: 396, h: 594, realW: 6,     realH: 9     },
  a5:          { w: 385, h: 546, realW: 5.83,  realH: 8.27  },
  pocket_4x7:  { w: 281, h: 454, realW: 4.25,  realH: 6.875 },
  us_letter:   { w: 561, h: 726, realW: 8.5,   realH: 11    },
};
const BINDING_SPINE_W = {
  perfect:       100,
  case_bound:    110,
  coil:           60,
  wire:           55,
  saddle_stitch:  12,
};

/**
 * BookCoverDesigner - Unified Wraparound Canvas
 * -----------------------------------------------
 * Single Fabric.js canvas holding all three surfaces:
 *   Back  [0 .. faceW]
 *   Spine [faceW .. faceW+spineW]
 *   Front [faceW+spineW .. 2*faceW+spineW]
 *
 * View switching: setDimensions + viewportTransform panning.
 * Objects wrap across faces in Full View and crop in single-face views.
 *
 * Six view tabs:
 *   Full View   - entire wraparound (all editable)
 *   Front Cover - front face only (clipped)
 *   Back Cover  - back face only (clipped)
 *   Spine       - spine strip only (clipped, portrait)
 *   On Shelf    - spine rotated -90 deg (horizontal preview)
 *   3D View     - CSS 3D preview from region snapshots
 */
export default function BookCoverDesigner() {
  const { id, versionId: urlVersionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // -- View switching ---------------------------------------------------------
  // 'full' | 'front' | 'back' | 'spine' | 'onshelf' | '3d'
  const [activeView, setActiveView] = useState('front');

  // -- 3D preview snapshots ---------------------------------------------------
  const [snapshots3d, setSnapshots3d] = useState({ front: null, spine: null, back: null });

  // -- Single unified canvas --------------------------------------------------
  const {
    canvasRef,
    canvas,
    currentVersionId,
    isAuthor,
    collaboratorRole,
    readOnly,
    versionData,
    setVersionData,
    isHydrating,
    hasIsbn: hasIsbnFromSetup,
    hasCoverImage,
    faceDims,
    setFaceDims,
  } = useCanvasSetup(id, urlVersionId);

  const { faceW, faceH, spineW } = faceDims;
  const totalW = faceW * 2 + spineW;

  // -- Autosave ---------------------------------------------------------------
  const { saveStatus, triggerChange, forceSave } = useCanvasAutosave(
    canvas, id, currentVersionId, readOnly, isHydrating, faceDims, hasCoverImage,
  );

  // -- Toolbar ref (for undo/redo via Ctrl+Z/Y) --------------------------------
  const toolbarRef = useRef(null);

  // -- 3D view ref (exposes rotateLeft/Right/resetView to controls rendered outside the scroll area) --
  const book3dRef = useRef(null);

  // -- Ruler refs + toggle ---------------------------------------------------
  const scrollRef       = useRef(null);
  const hRulerRef       = useRef(null);
  const vRulerRef       = useRef(null);
  const crosshairRef    = useRef(null);
  const coordReadoutRef = useRef(null);
  const [showRulers, setShowRulers] = useState(false);
  const [rulerUnit,  setRulerUnit]  = useState('in');

  // -- View switching logic ---------------------------------------------------
  const prevViewRef = useRef(activeView);

  // -- Canvas ready flag (hides canvas until applyView has run at least once) -
  const [canvasReady, setCanvasReady] = useState(false);

  // -- Canvas zoom (must be before applyView which captures zoom) ------------
  const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
  const ZOOM_MIN = 0.25;
  const ZOOM_MAX = 2;
  const [zoom, setZoom] = useState(1);
  const [zoomInputVal, setZoomInputVal] = useState('100');
  // Works correctly even when current zoom is not on a step (e.g. 0.96)
  const zoomIn    = () => setZoom(z => { const next = ZOOM_STEPS.find(s => s > z + 0.001); return next !== undefined ? next : z; });
  const zoomOut   = () => setZoom(z => { const prev = [...ZOOM_STEPS].reverse().find(s => s < z - 0.001); return prev !== undefined ? prev : z; });
  const zoomReset = () => setZoom(1);

  // Track whether the user is actively editing the zoom input
  const zoomInputFocused = useRef(false);

  // Keep the text input in sync only when the user is NOT editing it
  useEffect(() => {
    if (!zoomInputFocused.current) {
      setZoomInputVal(String(Math.round(zoom * 100)));
    }
  }, [zoom]);

  const applyZoomInput = () => {
    zoomInputFocused.current = false;
    const raw = parseInt(zoomInputVal, 10);
    if (isNaN(raw) || raw <= 0) {
      setZoomInputVal(String(Math.round(zoom * 100)));
      return;
    }
    // Clamp to [25, 200] but honour whatever the user typed — no snap to step
    const clamped = Math.min(ZOOM_MAX * 100, Math.max(ZOOM_MIN * 100, raw)) / 100;
    setZoom(clamped);
    setZoomInputVal(String(Math.round(clamped * 100)));
  };

  const applyView = useCallback((view) => {
    if (!canvas) return;
    const z = zoom;
    const vpt = [z, 0, 0, z, 0, 0];

    switch (view) {
      case 'full':
        canvas.setDimensions({ width: totalW * z, height: faceH * z });
        // vpt stays identity (pan = 0)
        break;
      case 'front':
        canvas.setDimensions({ width: faceW * z, height: faceH * z });
        vpt[4] = -(faceW + spineW) * z;
        break;
      case 'back':
        canvas.setDimensions({ width: faceW * z, height: faceH * z });
        // vpt pan stays 0 (back starts at x=0)
        break;
      case 'spine':
      case 'onshelf':
        canvas.setDimensions({ width: spineW * z, height: faceH * z });
        vpt[4] = -faceW * z;
        break;
      default:
        break;
    }

    canvas.viewportTransform = vpt;
    canvas.selection = !readOnly;
    canvas.requestRenderAll();
    setCanvasReady(true);
  }, [canvas, faceW, faceH, spineW, totalW, readOnly, zoom]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Apply view whenever activeView or dimensions change
  // Gated on !isHydrating so the canvas stays invisible until data has loaded
  useEffect(() => {
    if (!canvas || activeView === '3d' || isHydrating) return;
    applyView(activeView);
    prevViewRef.current = activeView;
  }, [activeView, canvas, applyView, isHydrating]);

  // -- Fold-line guides (Full View only) --------------------------------------
  useEffect(() => {
    if (!canvas) return;

    const drawFoldLines = () => {
      if (activeView !== 'full') return;

      const ctx = canvas.getContext('2d');
      const vpt = canvas.viewportTransform;

      ctx.save();
      ctx.setLineDash([8, 4]);
      ctx.strokeStyle = 'rgba(255, 80, 80, 0.55)';
      ctx.lineWidth = 1;

      // Left fold: between back and spine (at x = faceW)
      const x1 = faceW * vpt[0] + vpt[4];
      ctx.beginPath();
      ctx.moveTo(x1, 0);
      ctx.lineTo(x1, faceH);
      ctx.stroke();

      // Right fold: between spine and front (at x = faceW + spineW)
      const x2 = (faceW + spineW) * vpt[0] + vpt[4];
      ctx.beginPath();
      ctx.moveTo(x2, 0);
      ctx.lineTo(x2, faceH);
      ctx.stroke();

      ctx.restore();
    };

    canvas.on('after:render', drawFoldLines);
    return () => canvas.off('after:render', drawFoldLines);
  }, [canvas, activeView, faceW, faceH, spineW]);

  // -- Patch getPointer for On Shelf (CSS rotate -90deg) ----------------------
  useEffect(() => {
    if (!canvas) return;
    const upperCanvas = canvas.upperCanvasEl;
    if (!upperCanvas) return;

    if (activeView === 'onshelf') {
      const origGetPointer = canvas.getPointer;
      canvas._origGetPointer = origGetPointer;
      canvas.getPointer = function(e, ignoreZoom) {
        const rect = upperCanvas.getBoundingClientRect();
        const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
        const clientX = touch ? touch.clientX : (e.clientX || 0);
        const clientY = touch ? touch.clientY : (e.clientY || 0);
        const dx = clientX - rect.left;
        const dy = clientY - rect.top;
        // Rotate coords: canvas is spineW x faceH, visually rotated to faceH x spineW
        let cx = dy * (this.width / rect.height);
        let cy = this.height - dx * (this.height / rect.width);
        cx = Math.max(0, Math.min(this.width, cx));
        cy = Math.max(0, Math.min(this.height, cy));
        if (ignoreZoom) return new fabric.Point(cx, cy);
        const vpt2 = this.viewportTransform;
        return new fabric.Point((cx - vpt2[4]) / vpt2[0], (cy - vpt2[5]) / vpt2[3]);
      };
    } else if (canvas._origGetPointer) {
      canvas.getPointer = canvas._origGetPointer;
      delete canvas._origGetPointer;
    }
  }, [activeView, canvas]);

  // -- 3D snapshot capture (region exports) -----------------------------------
  const capture3dSnapshots = useCallback(() => {
    if (!canvas) return;

    // Temporarily set full view + identity viewport for accurate export
    const savedDims = { width: canvas.getWidth(), height: canvas.getHeight() };
    const savedVpt = [...canvas.viewportTransform];

    canvas.setDimensions({ width: totalW, height: faceH });
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];

    if (!canvas.backgroundColor || canvas.backgroundColor === 'transparent') {
      canvas.backgroundColor = '#ffffff';
    }
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const snap = (left, width) => {
      return canvas.toDataURL({
        format: 'png',
        multiplier: 2,
        left: left,
        top: 0,
        width: width,
        height: faceH,
      });
    };

    setSnapshots3d({
      back: snap(0, faceW),
      spine: snap(faceW, spineW),
      front: snap(faceW + spineW, faceW),
    });

    // Restore previous view
    canvas.setDimensions(savedDims);
    canvas.viewportTransform = savedVpt;
    canvas.requestRenderAll();
  }, [canvas, faceW, faceH, spineW, totalW]);

  // -- Text placement state ---------------------------------------------------
  const [placingText, setPlacingText] = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('');
  const [textAlign, setTextAlign] = useState('left');

  // -- Per-face background colors ---------------------------------------------
  const [faceBgColors, setFaceBgColors] = useState({ front: '#ffffff', spine: '#ffffff', back: '#ffffff' });

  // Hydrate faceBgColors from canvas bg-rect objects once canvas is ready.
  // This ensures the state is correct after reloading a saved design.
  useEffect(() => {
    if (!canvas || isHydrating) return;
    const readFill = (name) => {
      const obj = canvas.getObjects().find(o => o.name === name);
      return (obj && obj.fill) ? obj.fill : (canvas.backgroundColor || '#ffffff');
    };
    setFaceBgColors({
      front: readFill('bg-front'),
      spine: readFill('bg-spine'),
      back:  readFill('bg-back'),
    });
  }, [canvas, isHydrating]);

  // Sync faceBgColors from canvas after undo/redo restores a previous state.
  // Also re-reads canvas.backgroundColor for the Full View path (where the background
  // is stored directly on the canvas rather than in bg-rect objects).
  const handleUndoRedo = useCallback(() => {
    if (!canvas) return;
    const readFill = (name) => {
      const obj = canvas.getObjects().find(o => o.name === name);
      return (obj && typeof obj.fill === 'string') ? obj.fill : '#ffffff';
    };
    setFaceBgColors({
      front: readFill('bg-front'),
      spine: readFill('bg-spine'),
      back:  readFill('bg-back'),
    });
    // Force a re-render so the full-view background color picker picks up the
    // restored canvas.backgroundColor (read inline from canvas at render time).
    // setFaceBgColors above always produces a new object reference, which is
    // sufficient to trigger the re-render — no extra state needed.
  }, [canvas]);

  // -- Cover margins (visual guide lines) --------------------------------------
  const [coverMargins, setCoverMargins] = useState({ top: 0, right: 0, bottom: 0, left: 0 });

  // Restore margin guides after applyView has finished setting up the canvas.
  // canvasReady is set to true INSIDE applyView, so this always fires after
  // setDimensions / requestRenderAll have run — no more timing race.
  useEffect(() => {
    if (!canvasReady || !canvas || !canvas._coverMargins) return;
    const m = canvas._coverMargins;
    setCoverMargins(m);
    redrawMarginGuides(m, faceW, faceH, spineW);
  }, [canvasReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Spine thickness mode ('pages' | 'manual') --------------------------------
  const [thicknessMode, setThicknessMode] = useState('pages');

  // Restore saved thickness mode once hydration completes.
  useEffect(() => {
    if (!canvas || isHydrating) return;
    if (canvas._thicknessMode) setThicknessMode(canvas._thicknessMode);
  }, [canvas, isHydrating]);

  const handleThicknessModeChange = useCallback((mode) => {
    setThicknessMode(mode);
    if (canvas) canvas._thicknessMode = mode;
    triggerChange();
  }, [canvas, triggerChange]);

  // Draws dashed safe-zone lines for each face in canvas coordinate space.
  // Canvas layout: Back[0..faceW] | Spine[faceW..faceW+spineW] | Front[faceW+spineW..totalW]
  const redrawMarginGuides = useCallback((margins, fw, fh, sw) => {
    if (!canvas) return;
    const tw = fw * 2 + sw;

    // Remove old guides
    canvas.getObjects()
      .filter((o) => o.name === 'margin-guide')
      .forEach((o) => canvas.remove(o));

    const { top: mt, right: mr, bottom: mb, left: ml } = margins;
    if (mt === 0 && mr === 0 && mb === 0 && ml === 0) {
      canvas.requestRenderAll();
      return;
    }

    const guideStyle = {
      stroke: '#e040fb',
      strokeWidth: 1,
      strokeDashArray: [5, 4],
      opacity: 0.75,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      name: 'margin-guide',
    };

    // Draw a 4-line safe-zone rectangle for one face region
    const drawFaceGuides = (x0, x1) => {
      const gx0 = x0 + ml;   // inner left
      const gx1 = x1 - mr;   // inner right
      const gy0 = mt;         // inner top
      const gy1 = fh - mb;    // inner bottom
      if (gx1 <= gx0 || gy1 <= gy0) return; // degenerate — skip
      canvas.add(new fabric.Line([gx0, gy0, gx1, gy0], guideStyle)); // top
      canvas.add(new fabric.Line([gx0, gy1, gx1, gy1], guideStyle)); // bottom
      canvas.add(new fabric.Line([gx0, gy0, gx0, gy1], guideStyle)); // left
      canvas.add(new fabric.Line([gx1, gy0, gx1, gy1], guideStyle)); // right
    };

    drawFaceGuides(0, fw);           // back cover
    drawFaceGuides(fw, fw + sw);     // spine
    drawFaceGuides(fw + sw, tw);     // front cover

    canvas.requestRenderAll();
  }, [canvas]);

  const handleSetMargins = useCallback((margins) => {
    setCoverMargins(margins);
    if (canvas) canvas._coverMargins = margins;
    redrawMarginGuides(margins, faceW, faceH, spineW);
    // forceSave pushes to server immediately (same pattern as bookSize)
    forceSave(true);
  }, [canvas, redrawMarginGuides, faceW, faceH, spineW, forceSave]);  // -- Book size change (save to print-specs endpoint) ------------------------
  const handleBookSizeChange = useCallback(async (key) => {
    setVersionData((prev) => ({ ...prev, bookSize: key }));
    if (!currentVersionId) return;
    try {
      const token = localStorage.getItem('rv_access_token');
      await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/print-specs`, {
        method: 'PUT',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ bookSize: key }),
      });
    } catch (err) {
      console.warn('[BookCoverDesigner] Failed to save bookSize:', err);
    }
  }, [currentVersionId, id, setVersionData]);

  // Upload an image dropped on the canvas to S3 and return a stable app URL to
  // use as the Fabric image src. Keeps big images out of the design JSON (which
  // is stored inline in the draft doc and hits MongoDB's 16MB limit otherwise).
  // Returns null on failure so the caller can fall back to embedding base64.
  const uploadImageAsset = useCallback(async (dataUrl) => {
    if (!id) return null;
    try {
      const token = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/asset`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ image: dataUrl }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.assetId) return null;
      // Stable URL that 302-redirects to a fresh presigned S3 URL on every load.
      return `${API_BASE}/api/drafts/${id}/cover/asset/${data.assetId}`;
    } catch (err) {
      console.warn('[BookCoverDesigner] Image asset upload failed:', err);
      return null;
    }
  }, [id]);

  // Re-draw guides when cover dimensions change (e.g. spine thickness updated)
  useEffect(() => {
    redrawMarginGuides(coverMargins, faceW, faceH, spineW);
  }, [faceW, faceH, spineW]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Declarative bg-rect sync ----------------------------------------------
  // bg-rect geometry is DERIVED from faceDims. This guarantees the back / spine /
  // front background rectangles ALWAYS match the current face dimensions exactly,
  // regardless of how faceDims got updated (resize, checkpoint restore, initial
  // load, or any other path). It also recovers unnamed legacy bg-rects, removes
  // duplicates, and keeps them locked + at the bottom of the z-stack.
  useEffect(() => {
    if (!canvas) return;

    const targets = {
      'bg-back':  { left: 0,              top: 0, width: faceW,  height: faceH },
      'bg-spine': { left: faceW,          top: 0, width: spineW, height: faceH },
      'bg-front': { left: faceW + spineW, top: 0, width: faceW,  height: faceH },
    };

    // 1. Recover unnamed legacy bg-rects by matching their position to a face region.
    Object.entries(targets).forEach(([name, region]) => {
      if (canvas.getObjects().some(o => o.name === name)) return;
      const candidate = canvas.getObjects().find(o => {
        if (o.name || o.type !== 'rect') return false;
        const oW = (o.width || 0) * (o.scaleX || 1);
        const oH = (o.height || 0) * (o.scaleY || 1);
        return Math.abs((o.left || 0) - region.left) < 120 &&
               Math.abs(o.top || 0) < 30 &&
               oH > faceH * 0.4 &&
               oW > region.width * 0.4;
      });
      if (candidate) candidate.set({ name });
    });

    // 2. Remove duplicate bg-rects (keep the first one for each name).
    ['bg-back', 'bg-spine', 'bg-front'].forEach(name => {
      const all = canvas.getObjects().filter(o => o.name === name);
      all.slice(1).forEach(dup => canvas.remove(dup));
    });

    // 3. Snap each bg-rect to its derived face region (idempotent).
    // width/height are assigned as primitives + dirty:true to force Fabric to
    // re-rasterize the object cache. Using .set({width,height}) alongside a
    // left-shift was leaving bg-front at its old cached pixel dims.
    Object.entries(targets).forEach(([name, region]) => {
      const obj = canvas.getObjects().find(o => o.name === name);
      if (!obj) return;
      obj.set({
        left: region.left,
        top:  region.top,
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        skewX: 0,
        skewY: 0,
        selectable: false,
        evented: false,
      });
      obj.width  = region.width;
      obj.height = region.height;
      obj.dirty  = true;
      obj.setCoords();
    });

    // 4. Push all bg-rects to the bottom of the z-stack in canonical order.
    ['bg-back', 'bg-spine', 'bg-front'].forEach(name => {
      const o = canvas.getObjects().find(x => x.name === name);
      if (o) canvas.sendObjectToBack(o);
    });

    canvas.requestRenderAll();
  }, [canvas, faceW, faceH, spineW]);

  // -- Resize cover dimensions -------------------------------------------------
  // Scales every object proportionally to its face's new dimensions so nothing
  // gets cut off or pushed onto an adjacent face. Per-face horizontal ratios
  // differ (face vs spine), so face assignment uses object center-X.
  const handleResizeCover = useCallback(({ faceW: newFW, faceH: newFH, spineW: newSW }) => {
    if (!canvas) return;

    const oldFW = faceW, oldFH = faceH, oldSW = spineW;
    if (oldFW === newFW && oldFH === newFH && oldSW === newSW) return;

    const ratioH = newFH / oldFH;
    const ratioFace = newFW / oldFW;
    const ratioSpine = newSW / oldSW;
    const oldBackEnd = oldFW;
    const oldSpineEnd = oldFW + oldSW;
    const newSpineEnd = newFW + newSW;

    canvas.getObjects().forEach((obj) => {
      if (obj.name === 'margin-guide') return; // redrawn by deps useEffect
      // bg-rects are handled declaratively by the bg-rect sync useEffect
      // (driven by faceDims changes). Skip them here so they aren't double-scaled.
      if (obj.name === 'bg-back' || obj.name === 'bg-spine' || obj.name === 'bg-front') return;

      // Pick face by object center to be robust against partial overlaps
      const w = (obj.width || 0) * (obj.scaleX || 1);
      const objCenterX = (obj.left || 0) + w / 2;
      let face, oldFaceLeft, newFaceLeft, ratioX;
      if (objCenterX < oldBackEnd) {
        face = 'back';  oldFaceLeft = 0;            newFaceLeft = 0;            ratioX = ratioFace;
      } else if (objCenterX < oldSpineEnd) {
        face = 'spine'; oldFaceLeft = oldFW;        newFaceLeft = newFW;        ratioX = ratioSpine;
      } else {
        face = 'front'; oldFaceLeft = oldSpineEnd;  newFaceLeft = newSpineEnd;  ratioX = ratioFace;
      }

      const newLeft = newFaceLeft + ((obj.left || 0) - oldFaceLeft) * ratioX;
      const newTop  = (obj.top || 0) * ratioH;

      if (obj.type === 'textbox') {
        // Textboxes don't scale cleanly via scaleX/scaleY (see object:scaling
        // handler) — adjust width + fontSize directly instead.
        obj.set({
          left: newLeft,
          top:  newTop,
          width: (obj.width || 0) * ratioX,
          fontSize: (obj.fontSize || 16) * ratioH,
          scaleX: 1,
          scaleY: 1,
        });
      } else {
        obj.set({
          left: newLeft,
          top:  newTop,
          scaleX: (obj.scaleX || 1) * ratioX,
          scaleY: (obj.scaleY || 1) * ratioH,
        });
      }

      // Per-face clipPath needs to follow the new face region
      if (obj.clipPath) {
        const r = face === 'spine'
          ? { left: newFW, width: newSW }
          : face === 'front'
          ? { left: newSpineEnd, width: newFW }
          : { left: 0, width: newFW };
        obj.clipPath = new fabric.Rect({ ...r, top: 0, height: newFH, absolutePositioned: true });
      }

      obj.setCoords();
    });

    // Apply canvas dimensions synchronously with the new face dims.
    const z = zoom;
    const newTotalW = newFW * 2 + newSW;
    switch (activeView) {
      case 'full':
        canvas.setDimensions({ width: newTotalW * z, height: newFH * z });
        canvas.viewportTransform = [z, 0, 0, z, 0, 0];
        break;
      case 'front':
        canvas.setDimensions({ width: newFW * z, height: newFH * z });
        canvas.viewportTransform = [z, 0, 0, z, -(newFW + newSW) * z, 0];
        break;
      case 'back':
        canvas.setDimensions({ width: newFW * z, height: newFH * z });
        canvas.viewportTransform = [z, 0, 0, z, 0, 0];
        break;
      case 'spine':
      case 'onshelf':
        canvas.setDimensions({ width: newSW * z, height: newFH * z });
        canvas.viewportTransform = [z, 0, 0, z, -newFW * z, 0];
        break;
      default:
        break;
    }

    canvas.requestRenderAll();

    // Update React state so applyView useEffect stays in sync on subsequent renders.
    setFaceDims({ faceW: newFW, faceH: newFH, spineW: newSW });
    forceSave(true);
  }, [canvas, faceW, faceH, spineW, setFaceDims, forceSave, activeView, zoom]);

  // -- ISBN barcode state -----------------------------------------------------
  const [isbnOnCanvas, setIsbnOnCanvas] = useState(false);
  const DEFAULT_ISBN = '9780000000002';

  // Initialise from setup hook
  useEffect(() => {
    setIsbnOnCanvas(hasIsbnFromSetup);
  }, [hasIsbnFromSetup]);

  // Sync isbnOnCanvas when ISBN objects are added/removed at runtime
  useEffect(() => {
    if (!canvas) return;
    const handleAdded = (e) => {
      if (e.target && e.target.name === 'isbn-barcode') {
        setIsbnOnCanvas(true);
      }
    };
    const handleRemoved = (e) => {
      const obj = e.target;
      if (!obj) return;

      // Skip annotation cleanup during groupSelected — objects are just being
      // moved inside a group, not deleted. The flag is set in TextStyleToolbar.
      if (canvas._suppressAnnotationCleanup) return;

      // ISBN tracking
      if (obj.name === 'isbn-barcode') {
        const stillHas = canvas.getObjects().some(o => o.name === 'isbn-barcode');
        setIsbnOnCanvas(stillHas);
      }

      // Collect _collabId from the removed object AND any annotated children
      // (handles case where a group containing annotated objects is deleted).
      const collectCollabIds = (o) => {
        const ids = [];
        if (o._collabId) ids.push(o._collabId);
        if (typeof o.getObjects === 'function') {
          o.getObjects().forEach(child => ids.push(...collectCollabIds(child)));
        }
        return ids;
      };

      const collabIds = collectCollabIds(obj);
      if (collabIds.length > 0 && id && currentVersionId) {
        const token = localStorage.getItem('rv_access_token');
        collabIds.forEach(cid => {
          fetch(
            `${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/object-comments?collabId=${cid}`,
            { method: 'DELETE', credentials: 'omit', headers: token ? { Authorization: `Bearer ${token}` } : {} },
          ).catch(() => {});
          setObjCommentCounts(prev => { const next = { ...prev }; delete next[cid]; return next; });
          setActiveObjComment(prev => (prev?.collabId === cid ? null : prev));
        });
      }
    };
    canvas.on('object:added', handleAdded);
    canvas.on('object:removed', handleRemoved);
    return () => {
      canvas.off('object:added', handleAdded);
      canvas.off('object:removed', handleRemoved);
    };
  }, [canvas, id, currentVersionId]);

  // -- Blurb (fetched from draft) ---------------------------------------------
  const [draftBlurb, setDraftBlurb] = useState('');

  useEffect(() => {
    const fetchBlurb = async () => {
      try {
        const accessToken = localStorage.getItem('rv_access_token');
        const res = await fetch(API_BASE + '/api/drafts/' + id, {
          credentials: 'omit',
          headers: accessToken ? { Authorization: 'Bearer ' + accessToken } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setDraftBlurb(data.blurb || '');
        }
      } catch (err) {
        console.error('Failed to fetch blurb:', err);
      }
    };
    if (id) fetchBlurb();
  }, [id]);

  // -- Keyboard shortcuts (Ctrl+S, Ctrl+Shift+S) ------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        setShowCheckpointPrompt(true);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveProgress();
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        const activeObj = canvas?.getActiveObject();
        if (activeObj && activeObj.isEditing) return;
        e.preventDefault();
        toolbarRef.current?.undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        const activeObj = canvas?.getActiveObject();
        if (activeObj && activeObj.isEditing) return;
        e.preventDefault();
        toolbarRef.current?.redo();
      }
      if (e.key === 'Escape' && canvas) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvas, currentVersionId, isHydrating]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Permanently lock bg-rects (selection guard) ----------------------------
  // bg-rects must never be selectable. This runs on every selection event so
  // that even if a stale save loaded them without selectable:false, they are
  // immediately re-locked and deselected before the user can drag them.
  useEffect(() => {
    if (!canvas) return;

    const BG_NAMES = ['bg-front', 'bg-spine', 'bg-back'];

    const isBgRect = (obj) => {
      if (!obj) return false;
      if (BG_NAMES.includes(obj.name)) return true;
      // Positional fallback: full-face-height rect with top ≈ 0
      if (obj.type !== 'rect') return false;
      const oH = (obj.height || 0) * (obj.scaleY || 1);
      const oW = (obj.width  || 0) * (obj.scaleX || 1);
      return (obj.top || 0) < 5 && oH > faceH * 0.8 && oW > Math.min(faceW, spineW) * 0.5;
    };

    const lockAndDeselect = (e) => {
      const targets = e.selected || (e.target ? [e.target] : []);
      let changed = false;
      targets.forEach((obj) => {
        if (isBgRect(obj)) {
          obj.set({ selectable: false, evented: false });
          changed = true;
        }
      });
      if (changed) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
    };

    canvas.on('selection:created', lockAndDeselect);
    canvas.on('selection:updated', lockAndDeselect);
    return () => {
      canvas.off('selection:created', lockAndDeselect);
      canvas.off('selection:updated', lockAndDeselect);
    };
  }, [canvas, faceH, faceW, spineW]);

  // -- Determine which face region the pointer or active object is in ---------
  const getFaceAtX = useCallback((x) => {
    if (x < faceW) return 'back';
    if (x < faceW + spineW) return 'spine';
    return 'front';
  }, [faceW, spineW]);

  // -- Active face (per view) -------------------------------------------------
  const getActiveFace = useCallback(() => {
    if (activeView === 'front') return 'front';
    if (activeView === 'back') return 'back';
    if (activeView === 'spine' || activeView === 'onshelf') return 'spine';
    return null; // full / 3d — no single face
  }, [activeView]);

  // -- Per-face background setter ---------------------------------------------
  const handleSetFaceBackground = useCallback((color) => {
    if (!canvas) return;
    const face = getActiveFace();
    if (!face) {
      // Full view: paint the whole canvas background
      canvas.backgroundColor = color;
      canvas.requestRenderAll();
      triggerChange();
      return;
    }
    const regionMap = {
      front: { left: faceW + spineW, top: 0, width: faceW,  height: faceH },
      spine: { left: faceW,          top: 0, width: spineW, height: faceH },
      back:  { left: 0,              top: 0, width: faceW,  height: faceH },
    };
    const region = regionMap[face];
    const name = `bg-${face}`;

    // Remove ALL rects that look like old/orphaned bg rects for this face.
    // This prevents stacking when the name was lost after a save/reload.
    const orphans = canvas.getObjects().filter((o) => {
      if (o.name === name) return false; // keep the properly named one — we update it below
      if (o.type !== 'rect') return false;
      // Large rect sitting at the face region origin = old bg rect
      const oLeft = Math.round(o.left || 0);
      const oTop  = Math.round(o.top  || 0);
      const oW    = Math.round((o.width || 0) * (o.scaleX || 1));
      const oH    = Math.round((o.height || 0) * (o.scaleY || 1));
      return Math.abs(oLeft - region.left) < 5 &&
             Math.abs(oTop - region.top) < 5 &&
             Math.abs(oW - region.width) < 20 &&
             Math.abs(oH - region.height) < 20 &&
             !o.name; // only unnamed — don't touch user shapes with names
    });
    orphans.forEach((o) => canvas.remove(o));

    const existing = canvas.getObjects().find((o) => o.name === name);
    if (existing) {
      // Re-snap geometry and clear any stale clip path that may have been
      // accidentally applied when the rect was briefly nameless (e.g. during undo/redo).
      existing.clipPath = undefined;
      existing.set({
        ...region,
        scaleX: 1, scaleY: 1,
        visible: true, opacity: 1,
        fill: color,
        selectable: false, evented: false,
      });
      existing.dirty = true;
      canvas.requestRenderAll();
      canvas.fire('object:modified', { target: existing });
    } else {
      const rect = new fabric.Rect({
        ...region,
        fill: color,
        selectable: false,
        evented: false,
        name,
      });
      canvas.add(rect);
      canvas.sendObjectToBack(rect);
      canvas.requestRenderAll();
    }
    // Persist per-face color as a canvas-level backup so it survives even if
    // the bg-rect's `name` is lost on save/reload (index-based name recovery
    // can fail for rects, causing the fill to look "white" after refresh).
    canvas._faceBgColors = { ...(canvas._faceBgColors || {}), [face]: color };
    setFaceBgColors((prev) => ({ ...prev, [face]: color }));
    triggerChange();
  }, [canvas, getActiveFace, faceW, faceH, spineW, triggerChange]);

  // -- Auto-clip objects added in single-face views --------------------------
  const SYSTEM_NAMES = ['bg-front', 'bg-spine', 'bg-back', 'isbn-barcode'];

  const applyFaceClip = useCallback((obj) => {
    // The canvas flag as well as the React one: this runs on `object:added`,
    // which fires while a design is loading, and state can lag a render behind.
    // Missing that window re-scoped loaded artwork to the face on screen.
    if (isHydrating || obj?.canvas?._rvHydrating) return;
    if (obj.name && SYSTEM_NAMES.includes(obj.name)) return;
    if (obj.name && obj.name.startsWith('bg-')) return;
    // Guard: a non-selectable rect that fills the whole face height is a bg-rect
    // that may be briefly nameless during undo/redo loadFromJSON. Never clip it.
    if (obj.type === 'rect' && obj.selectable === false && (obj.top || 0) < 2 &&
        (obj.height || 0) * (obj.scaleY || 1) > faceH * 0.8) return;
    // Never overwrite an existing clipPath. Otherwise undo/redo (loadFromJSON)
    // would replace every object's saved clip with the currently-active face's
    // clip — which is wrong when an object lives on a different face than the
    // current view, leaving it invisible.
    if (obj.clipPath) return;
    const face = getActiveFace();
    if (!face) return; // full view → wrap, no clip
    const regionMap = {
      front: { left: faceW + spineW, top: 0, width: faceW,  height: faceH },
      spine: { left: faceW,          top: 0, width: spineW, height: faceH },
      back:  { left: 0,              top: 0, width: faceW,  height: faceH },
    };
    const r = regionMap[face];
    obj.clipPath = new fabric.Rect({ ...r, absolutePositioned: true });
  }, [isHydrating, getActiveFace, faceW, faceH, spineW]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canvas) return;
    const handleObjectAdded = (e) => { if (e.target) applyFaceClip(e.target); };
    canvas.on('object:added', handleObjectAdded);
    return () => canvas.off('object:added', handleObjectAdded);
  }, [canvas, applyFaceClip]);

  // -- Prevent textbox distortion on resize -----------------------------------
  useEffect(() => {
    if (!canvas) return;
    const handleScaling = (e) => {
      const obj = e.target;
      if (obj.type !== 'textbox') return;
      obj.set({ width: obj.width * obj.scaleX, scaleX: 1, scaleY: 1 });
    };
    canvas.on('object:scaling', handleScaling);
    return () => canvas.off('object:scaling', handleScaling);
  }, [canvas]);

  // Apply control visibility to textboxes after hydration (loaded from JSON)
  useEffect(() => {
    if (!canvas || isHydrating) return;
    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'textbox') {
        obj.setControlsVisibility({ mt: false, mb: false, tl: false, tr: false, bl: false, br: false });
      }
    });
    canvas.requestRenderAll();
  }, [canvas, isHydrating]);

  const handleRemoveFaceClip = useCallback(() => {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;
    obj.clipPath = null;
    canvas.requestRenderAll();
    triggerChange();
  }, [canvas, triggerChange]);

  const handleSetObjectFace = useCallback((face) => {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;
    if (face === 'all') {
      obj.clipPath = null;
    } else {
      const regionMap = {
        front: { left: faceW + spineW, top: 0, width: faceW,  height: faceH },
        spine: { left: faceW,          top: 0, width: spineW, height: faceH },
        back:  { left: 0,              top: 0, width: faceW,  height: faceH },
      };
      const r = regionMap[face];
      if (!r) return;
      obj.clipPath = new fabric.Rect({ ...r, absolutePositioned: true });
    }
    canvas.requestRenderAll();
    triggerChange();
  }, [canvas, faceW, faceH, spineW, triggerChange]);

  // -- Place text on click ----------------------------------------------------
  useEffect(() => {
    if (!canvas || !placingText) return;

    const handleClick = (opt) => {
      const pointer = canvas.getPointer(opt.e);
      const { x, y } = pointer;

      // Determine which face region we are in
      const face = getFaceAtX(x);
      const isSpine = face === 'spine';
      const regionLeft = face === 'back' ? 0 : face === 'spine' ? faceW : faceW + spineW;
      const regionWidth = isSpine ? spineW : faceW;

      const padding = 8;
      const clampedLeft = Math.min(x, regionLeft + regionWidth - padding * 2);
      const maxWidth = regionLeft + regionWidth - clampedLeft - padding;
      const defaultWidth = isSpine ? spineW - padding * 2 : 200;
      const textboxWidth = Math.min(defaultWidth, maxWidth);

      const textbox = new fabric.Textbox('Your Text Here', {
        left: clampedLeft,
        top: y,
        width: textboxWidth,
        fontSize: isSpine ? Math.min(fontSize, 14) : fontSize,
        fontFamily: fontFamily,
        fill: textColor,
        backgroundColor: bgColor,
        textAlign: textAlign,
        editable: true,
        padding: 5,
      });

      canvas.add(textbox);
      textbox.setControlsVisibility({ mt: false, mb: false, tl: false, tr: false, bl: false, br: false });
      canvas.setActiveObject(textbox);
      canvas.requestRenderAll();

      setTimeout(function() {
        textbox.enterEditing();
        if (textbox.hiddenTextarea) textbox.hiddenTextarea.focus();
      }, 50);

      setPlacingText(false);
    };

    canvas.on('mouse:down', handleClick);
    return () => canvas.off('mouse:down', handleClick);
  }, [canvas, placingText, fontSize, fontFamily, textColor, bgColor, textAlign, faceW, spineW, getFaceAtX]);

  /** Activate click-to-place text mode */
  const handleAddText = () => {
    if (!canvas) return;
    setPlacingText(true);
    canvas.defaultCursor = 'text';
  };

  /** Add blurb text to the back cover region */
  const handleAddBlurb = () => {
    if (!canvas) return;
    const blurbText = draftBlurb.trim() || 'Add your blurb here...';
    const padding = 20;
    // Back cover region: x = 0 to faceW
    const textbox = new fabric.Textbox(blurbText, {
      left: padding,
      top: faceH * 0.3,
      width: faceW - padding * 2,
      fontSize: 14,
      fontFamily: 'Georgia',
      fill: '#222222',
      textAlign: 'center',
      editable: true,
      padding: 5,
      splitByGrapheme: true,
    });
    canvas.add(textbox);
    textbox.setControlsVisibility({ mt: false, mb: false, tl: false, tr: false, bl: false, br: false });
    canvas.setActiveObject(textbox);
    canvas.requestRenderAll();
    triggerChange();
  };

  /** Add a sample ISBN barcode image to the back cover region */
  const handleAddISBN = () => {
    if (!canvas) return;
    // Enforce one barcode max
    if (canvas.getObjects().some(function(o) { return o.name === 'isbn-barcode'; })) return;

    // Render barcode onto an offscreen canvas
    const offscreen = document.createElement('canvas');
    try {
      JsBarcode(offscreen, DEFAULT_ISBN, {
        format: 'EAN13',
        width: 2,
        height: 70,
        displayValue: true,
        fontSize: 14,
        margin: 8,
        background: '#ffffff',
        lineColor: '#000000',
      });
    } catch (err) {
      console.error('JsBarcode error:', err);
      return;
    }

    const dataUrl = offscreen.toDataURL('image/png');
    const imgElement = new Image();
    imgElement.onload = function() {
      // Place in bottom-right of back cover region (x = 0..faceW)
      const fabricImg = new fabric.Image(imgElement, {
        left: faceW - imgElement.width * 0.55 - 16,
        top: faceH - imgElement.height * 0.55 - 16,
        scaleX: 0.55,
        scaleY: 0.55,
        selectable: !readOnly,
        evented: !readOnly,
        name: 'isbn-barcode',
      });
      canvas.add(fabricImg);
      canvas.setActiveObject(fabricImg);
      canvas.requestRenderAll();
      canvas._hasIsbn = true;
      setIsbnOnCanvas(true);
      triggerChange();
    };
    imgElement.src = dataUrl;
  };

  /** Remove the ISBN barcode from the canvas */
  const handleRemoveISBN = () => {
    if (!canvas) return;
    const barcodes = canvas.getObjects().filter(function(o) { return o.name === 'isbn-barcode'; });
    barcodes.forEach(function(o) { canvas.remove(o); });
    canvas._hasIsbn = false;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setIsbnOnCanvas(false);
    triggerChange();
  };

  /** Manual save (with alert confirmation) */
  const handleSaveProgress = () => {
    if (isHydrating) return;
    if (!currentVersionId) {
      toast.error('No version selected. Please go back and select a version.');
      return;
    }
    forceSave(false);
  };

  /** Export all cover images (front + spine + back) to backend */
  const handleExport = async () => {
    if (isHydrating) return;
    if (!currentVersionId) {
      toast.error('No version selected. Please go back and select a version.');
      return;
    }

    const accessToken = localStorage.getItem('rv_access_token');
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (accessToken) headers.Authorization = 'Bearer ' + accessToken;

    // Preserve the current view so we can restore it on every exit path.
    const savedDims = { width: canvas.getWidth(), height: canvas.getHeight() };
    const savedVpt = [...canvas.viewportTransform];
    const restoreView = () => {
      canvas.setDimensions(savedDims);
      canvas.viewportTransform = savedVpt;
      canvas.requestRenderAll();
    };

    try {
      // Temporarily set full view for accurate region exports
      canvas.setDimensions({ width: totalW, height: faceH });
      canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
      if (!canvas.backgroundColor || canvas.backgroundColor === 'transparent') {
        canvas.backgroundColor = '#ffffff';
        canvas.requestRenderAll();
      }
      canvas.discardActiveObject();
      canvas.requestRenderAll();

      // Build the unified wraparound design JSON — the source of truth.
      const design = canvas.toJSON(['name', '_collabId', '_locked', '_borderStyle']);
      design.backgroundColor = canvas.backgroundColor || design.backgroundColor || '#ffffff';
      design.faceDims = { faceW, faceH, spineW };
      if (canvas._hasIsbn !== undefined) design.hasIsbn = !!canvas._hasIsbn;

      // 1) Save the design JSON FIRST. This is the real data; if it fails, stop
      //    and keep the editor open so the user doesn't lose work.
      let designRes;
      try {
        designRes = await fetch(
          API_BASE + '/api/drafts/' + id + '/cover/versions/' + currentVersionId + '/wraparound-design',
          { method: 'PUT', credentials: 'omit', headers: headers, body: JSON.stringify({ design: design }) },
        );
      } catch (e) {
        restoreView();
        console.error('Error saving cover design:', e);
        toast.error('Failed to save cover. Please check your connection and try again.');
        return;
      }
      if (!designRes.ok) {
        restoreView();
        const err = await designRes.json().catch(() => ({}));
        toast.error(err.error || 'Failed to save cover design.');
        return;
      }

      // 2) Face preview images (thumbnails / 3D shelf / PDF) are best-effort.
      //    If image storage (S3) is unavailable they fail — warn but don't block.
      const saveRegion = async function(left, width, endpoint) {
        let dataUrl;
        try {
          dataUrl = canvas.toDataURL({
            format: 'jpeg', quality: 0.92, multiplier: 2,
            left: left, top: 0, width: width, height: faceH,
          });
        } catch (e) {
          // A cross-origin image without CORS taints the canvas → export throws.
          console.warn('[handleExport] preview export failed for ' + endpoint, e);
          return false;
        }
        try {
          const res = await fetch(
            API_BASE + '/api/drafts/' + id + '/cover/versions/' + currentVersionId + '/' + endpoint,
            { method: 'PUT', credentials: 'omit', headers: headers, body: JSON.stringify({ image: dataUrl, design: design }) },
          );
          return res.ok;
        } catch (e) {
          console.warn('[handleExport] preview upload failed for ' + endpoint, e);
          return false;
        }
      };

      const frontOk = await saveRegion(faceW + spineW, faceW, 'image');
      const spineOk = await saveRegion(faceW, spineW, 'spine-image');
      const backOk  = await saveRegion(0, faceW, 'back-image');

      restoreView();

      if (frontOk && spineOk && backOk) {
        toast.success('Cover saved!');
      } else {
        // Design is safely saved; only the preview images could not be generated.
        toast.info('Cover saved. Preview images could not be generated right now — they will refresh once image storage is available.');
      }
      navigate(-1);
    } catch (err) {
      restoreView();
      console.error('Error saving cover:', err);
      toast.error('Failed to save cover. Check your server logs.');
    }
  };

  // -- Save status display ----------------------------------------------------
  const saveStatusConfig = {
    saved: { text: '\u2713 Saved', color: '#10b981' },
    saving: { text: '\u23F3 Saving...', color: '#f59e0b' },
    unsaved: { text: '\u25CF Unsaved changes', color: '#ef4444' },
  };
  const statusInfo = saveStatusConfig[saveStatus] || saveStatusConfig.saved;

  // -- Sidebar panel state (mirrors EditorPage / EditorSidebar pattern) -------
  const [activePanel, setActivePanel] = useState('cover');
  const handlePanelClick = (panel) => {
    setActivePanel(prev => {
      const next = prev === panel ? null : panel;
      // When leaving the draw tab (switching away OR toggling it off), kill
      // drawing mode so the canvas doesn't stay in pencil mode after the
      // toolbar unmounts.
      if (prev === 'draw' && next !== 'draw' && canvas) {
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.defaultCursor = 'default';
        canvas.hoverCursor = 'move';
        const locked = ['bg-front','bg-spine','bg-back','margin-guide','isbn-barcode','eraser-path','draw-stroke'];
        const isFreehand = (o) => {
          if (o.name === 'draw-stroke' || o.name === 'eraser-path') return true;
          if (o.type === 'path' && !o.name) return true;
          if (o.type === 'group' && !o.name) return true;
          if (o.type === 'circle' && !o.name) return true;
          return false;
        };
        canvas.forEachObject(o => {
          const keep = (o.name && (locked.includes(o.name) || o.name.startsWith('bg-'))) || isFreehand(o);
          o.selectable = !keep;
          o.evented    = !keep;
        });
        canvas.requestRenderAll();
      }
      return next;
    });
    // Close collab side panels when switching away from collab tab
    if (panel !== 'collab') setCollabSideView(null);
  };

  // -- Checkpoint state -------------------------------------------------------
  const [checkpoints, setCheckpoints] = useState([]);
  const [showCheckpointPrompt, setShowCheckpointPrompt] = useState(false);
  const [checkpointLabel, setCheckpointLabel] = useState('');
  const [checkpointSaving, setCheckpointSaving] = useState(false);
  const [checkpointRestoring, setCheckpointRestoring] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  // Restore confirmation modal: { id, label, savedByName, savedByRole, createdAt } or null
  const [restoreModal, setRestoreModal] = useState(null);
  // Shown when the user tries to create checkpoint #11. Clicking Okay opens the timeline.
  const [showCheckpointLimitModal, setShowCheckpointLimitModal] = useState(false);
  const CHECKPOINT_LIMIT = 10;

  // -- PDF preview modal -------------------------------------------------------
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfGenerating, setPdfGenerating]   = useState(false);
  const [pdfPages, setPdfPages]             = useState([]);

  // Fetch checkpoints on mount / version change
  useEffect(() => {
    if (!id || !currentVersionId) return;
    const token = localStorage.getItem('rv_access_token');
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/checkpoints`, { credentials: 'omit', headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCheckpoints(d.checkpoints || []); })
      .catch(() => {});
  }, [id, currentVersionId]);

  /** Save a named checkpoint */
  const handleSaveCheckpoint = useCallback(async (label) => {
    if (!id || !currentVersionId) return;
    setCheckpointSaving(true);
    try {
      // Force-save current canvas state first
      await forceSave(false);
      const token = localStorage.getItem('rv_access_token');
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const res = await fetch(
        `${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/checkpoints`,
        { method: 'POST', credentials: 'omit', headers, body: JSON.stringify({ label: label || '' }) },
      );
      if (res.ok) {
        const data = await res.json();
        setCheckpoints(prev => [data.checkpoint, ...prev]);
        setShowCheckpointPrompt(false);
        setCheckpointLabel('');
      } else if (res.status === 409) {
        // Server enforces the cap — surface the limit-reached modal.
        setShowCheckpointPrompt(false);
        setCheckpointLabel('');
        setShowCheckpointLimitModal(true);
      }
    } catch (err) {
      console.error('Checkpoint save failed:', err);
    } finally {
      setCheckpointSaving(false);
    }
  }, [id, currentVersionId, forceSave]);

  /** Click handler for the "✦ New Checkpoint" button — short-circuits to the
   *  limit modal so the user doesn't have to type a label only to be rejected. */
  const handleNewCheckpointClick = useCallback(() => {
    if (checkpoints.length >= CHECKPOINT_LIMIT) {
      setShowCheckpointLimitModal(true);
      return;
    }
    setShowCheckpointPrompt(true);
  }, [checkpoints.length]);

  /** Open restore confirmation modal (called from timeline or activity tab) */
  const requestRestoreCheckpoint = useCallback((checkpointId) => {
    // Find checkpoint details for the modal
    const cp = checkpoints.find(c => c.id === checkpointId);
    setRestoreModal(cp || { id: checkpointId, label: 'checkpoint', savedByName: '', savedByRole: '', createdAt: '' });
  }, [checkpoints]);

  /**
   * Apply a checkpoint snapshot to the canvas.
   * Restores design, book size, and spine width — everything resets proportionally.
   * Used by both the confirm-modal path and the timeline modal's onRestored callback.
   */
  const applyCheckpointSnap = useCallback(async (snap) => {
    if (!canvas || !snap) return;
    const design = snap.wraparoundDesign || snap.coverDesign;
    if (!design) return;

    const parsed = typeof design === 'string' ? JSON.parse(design) : design;

    // Resolve print specs from snap (backend may include them) or fall back to current version
    const bookSizeKey = snap.bookSize || snap.printSpecs?.bookSize || versionData.bookSize || 'half_letter';
    const bindingKey  = snap.bindingType || snap.printSpecs?.bindingType || versionData.bindingType || 'perfect';
    const dims  = BOOK_SIZE_DIMS[bookSizeKey] || BOOK_SIZE_DIMS.half_letter;
    const spW   = BINDING_SPINE_W[bindingKey] || 100;
    const newFW = dims.w, newFH = dims.h;

    // Set canvas to full-width identity view so objects load at their saved coords
    canvas.setDimensions({ width: newFW * 2 + spW, height: newFH });
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];

    // Load design (Fabric v6 returns a Promise — don't use callback form)
    await canvas.loadFromJSON(parsed);
    canvas.backgroundColor = parsed.backgroundColor || parsed.background || '#ffffff';

    // Restore lock state for each object
    canvas.getObjects().forEach((obj) => {
      if (obj._locked) {
        obj.set({
          lockMovementX: true,
          lockMovementY: true,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
          lockSkewingX: true,
          lockSkewingY: true,
          hasControls: false,
        });
        if (obj.type === 'textbox' || obj.type === 'i-text') {
          obj.set({ editable: false });
        }
      }
      if (obj.name === 'border-rect') {
        obj.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
      }
    });

    // Lock all bg-rects and recover any that lost their name
    const BG_FACE_REGIONS = [
      { name: 'bg-back',  left: 0,         width: newFW },
      { name: 'bg-spine', left: newFW,     width: spW   },
      { name: 'bg-front', left: newFW+spW, width: newFW },
    ];
    BG_FACE_REGIONS.forEach(({ name, left, width }) => {
      const named = canvas.getObjects().find(o => o.name === name);
      if (named) {
        named.set({ selectable: false, evented: false });
        return;
      }
      const candidate = canvas.getObjects().find(o => {
        if (o.name || o.type !== 'rect') return false;
        const oW = (o.width||0)*(o.scaleX||1), oH = (o.height||0)*(o.scaleY||1);
        return Math.abs((o.left||0)-left)<10 && (o.top||0)<10 && Math.abs(oW-width)<30 && Math.abs(oH-newFH)<30;
      });
      if (candidate) candidate.set({ name, selectable: false, evented: false });
    });

    canvas.requestRenderAll();

    // Update size state — applyView useEffect re-fires and crops/pans the viewport correctly
    setFaceDims({ faceW: newFW, faceH: newFH, spineW: spW });
    setVersionData(prev => ({ ...prev, bookSize: bookSizeKey, bindingType: bindingKey }));
    triggerChange();
  }, [canvas, versionData, setFaceDims, setVersionData, triggerChange]);

  /** Actually restore the checkpoint (called when modal confirms) */
  const confirmRestoreCheckpoint = useCallback(async () => {
    if (!restoreModal || !id || !currentVersionId || !canvas) return;
    const checkpointId = restoreModal.id;
    setCheckpointRestoring(true);
    setRestoreModal(null);
    const token = localStorage.getItem('rv_access_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/checkpoints/${checkpointId}/restore`,
        { method: 'POST', credentials: 'omit', headers },
      );
      if (res.ok) {
        const data = await res.json();
        if (data.skipped) {
          toast.info(data.message || 'Design already matches this checkpoint.');
        } else {
          await applyCheckpointSnap(data.restoredDesign || {});
        }
        // Refresh checkpoint list
        const listRes = await fetch(
          `${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/checkpoints`,
          { credentials: 'omit', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
        );
        if (listRes.ok) {
          const ld = await listRes.json();
          setCheckpoints(ld.checkpoints || []);
        }
      }
    } catch (err) {
      console.error('Checkpoint restore failed:', err);
    } finally {
      setCheckpointRestoring(false);
    }
  }, [restoreModal, id, currentVersionId, canvas, applyCheckpointSnap]);

  // -- Collaborate side-panel state (right sidebar driven by Collaborate tab) -
  // 'collaborators' | 'comments' | 'changes' | null
  const [collabSideView, setCollabSideView] = useState(null);
  const [inviteSignal, setInviteSignal] = useState(0);

  // -- Object-level comment system -------------------------------------------
  const [objCommentCounts, setObjCommentCounts] = useState({});  // { collabId: count }
  const [activeObjComment, setActiveObjComment] = useState(null); // { collabId, label }

  const { generatePdfData } = usePdfGenerator({
    canvas,
    faceDims,
    objCommentCounts,
    draftId:     id,
    versionId:   currentVersionId,
    versionName: versionData?.name,
  });

  /** Open the PDF preview modal — generates all page data then shows the preview. */
  const handleOpenPdfPreview = useCallback(async () => {
    setShowPdfPreview(true);
    setPdfGenerating(true);
    setPdfPages([]);
    try {
      const { pages } = await generatePdfData();
      setPdfPages(pages);
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF preview. Please try again.');
      setShowPdfPreview(false);
    } finally {
      setPdfGenerating(false);
    }
  }, [generatePdfData, toast]);

  // Fetch comment count summary once canvas is ready
  useEffect(() => {
    if (!id || !currentVersionId) return;
    const token = localStorage.getItem('rv_access_token');
    fetch(
      `${API_BASE}/api/drafts/${id}/cover/versions/${currentVersionId}/object-comments/summary`,
      { credentials: 'omit', headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.counts) setObjCommentCounts(d.counts); })
      .catch(() => {});
  }, [id, currentVersionId]);

  // Context menu state
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, fabricObj, count }

  // Badge renderer
  useCommentBadges(canvas, objCommentCounts);

  // -- Highlight the object whose comment panel is open ----------------------
  // Fabric's own selection handles (setActiveObject) serve as the highlight.
  // We just need to select the object when the panel opens and deselect on close.
  useEffect(() => {
    if (!canvas) return;

    if (activeObjComment?.collabId) {
      const obj = canvas.getObjects().find(o => o._collabId === activeObjComment.collabId);
      if (obj) {
        canvas.setActiveObject(obj);
        canvas.requestRenderAll();
      }
    }
  }, [activeObjComment, canvas]);

  // Open the object comment panel for a hovered element
  const handleObjectCommentClick = useCallback((fabricObj) => {
    if (!fabricObj) return;
    // Assign _collabId if missing, then immediately flush to server so the ID
    // survives a refresh without waiting for the 60-second autosave cycle.
    if (!fabricObj._collabId) {
      fabricObj._collabId = 'el_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
      forceSave(true);
    }
    const label = fabricObj.name || fabricObj.text?.slice(0, 20) || fabricObj.type || 'Element';
    setActiveObjComment({ collabId: fabricObj._collabId, label });
    setCollabSideView(null); // close other side panels
  }, [forceSave]);

  // Navigate to the face that contains a canvas object
  // Returns the target view key, or null if already visible
  const faceViewForObj = useCallback((obj) => {
    if (!obj || activeView === 'full') return null; // full view shows everything
    const cx = (obj.left || 0) + ((obj.width  || 0) * (obj.scaleX || 1)) / 2;
    let targetView;
    if      (cx < faceW)           targetView = 'back';
    else if (cx < faceW + spineW)  targetView = 'spine';
    else                           targetView = 'front';
    return targetView === activeView ? null : targetView;
  }, [activeView, faceW, spineW]);

  // Called when user clicks an annotation group in the sidebar
  const handleHighlightObject = useCallback(({ collabId, label }) => {
    setActiveObjComment({ collabId, label });
    setCollabSideView(null);

    if (!canvas) return;
    const obj = canvas.getObjects().find(o => o._collabId === collabId);
    if (!obj) return;

    // Switch to the face where the object lives (skip in full view)
    const targetView = faceViewForObj(obj);
    if (targetView) setActiveView(targetView);

    // Select the object so it's visibly highlighted
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
  }, [canvas, faceViewForObj, setActiveView]);

  // ── Notification deep link ───────────────────────────────────────────────
  // /bookcover/:id/designer/:versionId?collab=<collabId> — sent by a cover
  // comment notification.
  //
  // Resolving it is a retry, not a single shot. `isHydrating` can report
  // finished while the canvas is still empty — a first load cancelled by
  // StrictMode's double-mount takes the `!json || cancelled` path in
  // useCanvasSetup and clears the flag without ever applying a design, and a
  // slow fetch does the same thing in production. Acting on that first settle
  // burns the deep link against an empty canvas, so instead keep the id and
  // look again on each settle until the object turns up or the window closes.
  const deepLinkCollabId = searchParams.get('collab');
  const pendingCollabRef = useRef(null);
  const deepLinkDoneRef = useRef(false);
  const deepLinkDeadlineRef = useRef(0);

  useEffect(() => {
    if (!deepLinkCollabId || deepLinkDoneRef.current) return;
    // Hold the id outside the URL, which is cleared immediately so it can't
    // re-fire and so a stale link stops advertising a missing element.
    pendingCollabRef.current = deepLinkCollabId;
    if (!deepLinkDeadlineRef.current) deepLinkDeadlineRef.current = Date.now() + 15000;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('collab');
      return next;
    }, { replace: true });
  }, [deepLinkCollabId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canvas || !pendingCollabRef.current || deepLinkDoneRef.current) return;

    // One interval, owned by this effect, rather than a timeout rescheduled on
    // every render: `isHydrating` and the canvas identity churn during load, and
    // a timeout recreated on each of those changes gets cleared before it ever
    // fires — the deep link then resolves only if the object happens to be
    // present on some unrelated re-run, which is how this came to work
    // intermittently rather than reliably.
    const finish = () => { deepLinkDoneRef.current = true; pendingCollabRef.current = null; };

    const tryResolve = () => {
      const collabId = pendingCollabRef.current;
      if (!collabId || deepLinkDoneRef.current) return true;

      const obj = canvas.getObjects().find((o) => o._collabId === collabId);
      if (obj) {
        finish();
        // Same heading the panel shows when the element is clicked directly.
        const label = obj.name || obj.text?.slice(0, 20) || obj.type || 'Element';
        handleHighlightObject({ collabId, label });
        return true;
      }

      // Out of time: the element really is gone, or was never on this version.
      // Leave the designer open on the right cover rather than opening a
      // comment panel on nothing.
      if (Date.now() > deepLinkDeadlineRef.current) { finish(); return true; }
      return false;
    };

    if (tryResolve()) return undefined;
    const iv = setInterval(() => { if (tryResolve()) clearInterval(iv); }, 300);
    return () => clearInterval(iv);
    // Deliberately only `canvas`: the polling above covers hydration timing, so
    // re-running this on every isHydrating flip would just restart the clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);

  // Close the object comment panel and clear highlight
  const handleCloseObjComment = useCallback(() => {
    setActiveObjComment(null);
    if (canvas) {
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
  }, [canvas]);

  // Update counts when ObjectCommentPanel reports a change
  const handleObjCountChange = useCallback((collabId, newCount) => {
    setObjCommentCounts(prev => ({ ...prev, [collabId]: newCount }));
    if (canvas) canvas.requestRenderAll(); // refresh badges
  }, [canvas]);

  // -- Rulers ----------------------------------------------------------------
  // Hide in views where the canvas is rotated or replaced (onshelf / 3d).
  const rulerVisible = showRulers && activeView !== 'onshelf' && activeView !== '3d';
  useRuler({ hRulerRef, vRulerRef, crosshairRef, coordReadoutRef, scrollRef, canvas, zoom, activeView, faceDims, showRulers: rulerVisible, unit: rulerUnit });

  // -- Face labels for Full View ----------------------------------------------
  const faceLabel = activeView === 'full' ? null : (
    activeView === 'front' ? 'Front Cover' :
    activeView === 'back' ? 'Back Cover' :
    activeView === 'spine' || activeView === 'onshelf' ? 'Spine' : null
  );

  return (
    <div className="designer-page">
      {/* ── Category tab bar + tools panel (sticky) ── */}
      {!readOnly && activeView !== '3d' && (
        <div className="cover-controls-sticky">
          <div className="cover-sidebar-top">
            {[
              { key: 'cover',     label: 'COVER' },
              { key: 'text',      label: 'TEXT' },
              { key: 'object',    label: 'OBJECT' },
              { key: 'draw',      label: 'DRAW' },
              { key: 'collab',    label: 'COLLABORATE' },
              { key: 'progress',  label: 'PROGRESS' },
            ].map(tab => (
              <button
                key={tab.key}
                className={`cover-tab-button${activePanel === tab.key ? ' active' : ''}`}
                onClick={() => handlePanelClick(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Side panel with existing tools, filtered by active tab ── */}
          {activePanel && (
          <div className="cover-panel-area">
          {/* Cover panel: background, margins, dims, thickness, ISBN, blurb */}
          {activePanel === 'cover' && (
            <TextStyleToolbar
              ref={toolbarRef}
              canvas={canvas}
              onAddText={handleAddText}
              fontSize={fontSize}
              setFontSize={setFontSize}
              fontFamily={fontFamily}
              setFontFamily={setFontFamily}
              textColor={textColor}
              setTextColor={setTextColor}
              bgColor={bgColor}
              setBgColor={setBgColor}
              textAlign={textAlign}
              setTextAlign={setTextAlign}
              onCanvasChange={triggerChange}
              onUploadImageAsset={uploadImageAsset}
              onSetFaceBackground={handleSetFaceBackground}
              onRemoveFaceClip={handleRemoveFaceClip}
              onSetObjectFace={handleSetObjectFace}
              faceBackgroundColor={(() => {
                const face = getActiveFace();
                if (!face) return canvas?.backgroundColor || '#ffffff';
                return faceBgColors[face] || '#ffffff';
              })()}
              activePanel="cover"
              onUndoRedo={handleUndoRedo}
              isHydrating={isHydrating}
              onAddISBN={handleAddISBN}
              onRemoveISBN={handleRemoveISBN}
              isbnOnCanvas={isbnOnCanvas}
              onAddBlurb={handleAddBlurb}
              draftBlurb={draftBlurb}
              activeView={activeView}
              faceDims={faceDims}
              onResizeCover={handleResizeCover}
              coverMargins={coverMargins}
              onSetMargins={handleSetMargins}
              bookSize={versionData.bookSize}
              onBookSizeChange={handleBookSizeChange}
              onDownloadPDF={handleOpenPdfPreview}
              thicknessMode={thicknessMode}
              onThicknessModeChange={handleThicknessModeChange}
            />
          )}

          {/* Text panel: font, style, align, orient, text colors */}
          {activePanel === 'text' && (
            <TextStyleToolbar
              ref={toolbarRef}
              canvas={canvas}
              onAddText={handleAddText}
              fontSize={fontSize}
              setFontSize={setFontSize}
              fontFamily={fontFamily}
              setFontFamily={setFontFamily}
              textColor={textColor}
              setTextColor={setTextColor}
              bgColor={bgColor}
              setBgColor={setBgColor}
              textAlign={textAlign}
              setTextAlign={setTextAlign}
              onCanvasChange={triggerChange}
              onUploadImageAsset={uploadImageAsset}
              onSetFaceBackground={handleSetFaceBackground}
              onRemoveFaceClip={handleRemoveFaceClip}
              onSetObjectFace={handleSetObjectFace}
              faceBackgroundColor={(() => {
                const face = getActiveFace();
                if (!face) return canvas?.backgroundColor || '#ffffff';
                return faceBgColors[face] || '#ffffff';
              })()}
              activePanel="text"
              onUndoRedo={handleUndoRedo}
              isHydrating={isHydrating}
            />
          )}

          {/* Object panel: object actions, colors, opacity */}
          {activePanel === 'object' && (
            <TextStyleToolbar
              ref={toolbarRef}
              canvas={canvas}
              onAddText={handleAddText}
              fontSize={fontSize}
              setFontSize={setFontSize}
              fontFamily={fontFamily}
              setFontFamily={setFontFamily}
              textColor={textColor}
              setTextColor={setTextColor}
              bgColor={bgColor}
              setBgColor={setBgColor}
              textAlign={textAlign}
              setTextAlign={setTextAlign}
              onCanvasChange={triggerChange}
              onUploadImageAsset={uploadImageAsset}
              onSetFaceBackground={handleSetFaceBackground}
              onRemoveFaceClip={handleRemoveFaceClip}
              onSetObjectFace={handleSetObjectFace}
              faceBackgroundColor={(() => {
                const face = getActiveFace();
                if (!face) return canvas?.backgroundColor || '#ffffff';
                return faceBgColors[face] || '#ffffff';
              })()}
              activePanel="object"
              onUndoRedo={handleUndoRedo}
              isHydrating={isHydrating}
              faceDims={faceDims}
            />
          )}

          {/* Draw panel: freehand drawing tools */}
          {activePanel === 'draw' && (
            <TextStyleToolbar
              ref={toolbarRef}
              canvas={canvas}
              onAddText={handleAddText}
              fontSize={fontSize}
              setFontSize={setFontSize}
              fontFamily={fontFamily}
              setFontFamily={setFontFamily}
              textColor={textColor}
              setTextColor={setTextColor}
              bgColor={bgColor}
              setBgColor={setBgColor}
              textAlign={textAlign}
              setTextAlign={setTextAlign}
              onCanvasChange={triggerChange}
              onUploadImageAsset={uploadImageAsset}
              onSetFaceBackground={handleSetFaceBackground}
              onRemoveFaceClip={handleRemoveFaceClip}
              onSetObjectFace={handleSetObjectFace}
              faceBackgroundColor={(() => {
                const face = getActiveFace();
                if (!face) return canvas?.backgroundColor || '#ffffff';
                return faceBgColors[face] || '#ffffff';
              })()}
              activePanel="draw"
              onUndoRedo={handleUndoRedo}
              isHydrating={isHydrating}
              activeView={activeView}
            />
          )}

          {/* Collaborate panel: full tool set */}
          {activePanel === 'collab' && (
            <CollaboratePanel
              collabSideView={collabSideView}
              setCollabSideView={setCollabSideView}
              onInvite={() => setInviteSignal(s => s + 1)}
            />
          )}

          {/* Progress panel: save actions + checkpoint timeline */}
          {activePanel === 'progress' && (
            <div className="text-style-toolbar-fixed prog-panel">
              <div className="prog-status-bar">
                <span className="prog-status-dot" style={{ background: statusInfo.color }} />
                <span className="prog-status-text" style={{ color: statusInfo.color }}>{statusInfo.text}</span>
              </div>

              <div className="prog-grid">
                {/* Save & Version Control */}
                <div className="prog-card">
                  <div className="prog-card-header">
                    <span className="prog-card-icon">💾</span>
                    <span className="prog-card-title">Save</span>
                  </div>
                  <div className="prog-card-body">
                    <button className="prog-btn prog-btn--primary" onClick={handleSaveProgress} disabled={isHydrating}>
                      <span className="prog-btn-icon">💾</span> Save Progress
                    </button>
                    <button className="prog-btn prog-btn--accent" onClick={handleExport} disabled={isHydrating}>
                      <span className="prog-btn-icon">📤</span> Save &amp; Close
                    </button>
                  </div>
                </div>

                {/* Checkpoints */}
                <div className="prog-card">
                  <div className="prog-card-header">
                    <span className="prog-card-icon">📌</span>
                    <span className="prog-card-title">Checkpoints</span>
                  </div>
                  <div className="prog-card-body">
                    <button className="prog-btn prog-btn--checkpoint" onClick={handleNewCheckpointClick}>
                      <span className="prog-btn-icon">✦</span> New Checkpoint
                    </button>
                    <button className="prog-btn prog-btn--outline" onClick={() => setShowTimelineModal(true)}>
                      <span className="prog-btn-icon">🕑</span> Timeline{checkpoints.length > 0 ? ` (${checkpoints.length})` : ''}
                    </button>
                  </div>
                </div>

                {/* Tasks */}
                <div className="prog-card">
                  <div className="prog-card-header">
                    <span className="prog-card-icon">📋</span>
                    <span className="prog-card-title">Tasks</span>
                  </div>
                  <div className="prog-card-body">
                    <button className="prog-btn prog-btn--outline" onClick={() => setCollabSideView('tasks')}>
                      <span className="prog-btn-icon">📋</span> {isAuthor ? 'Manage Tasks' : 'View Tasks'}
                    </button>
                  </div>
                </div>

                {/* Status */}
                <div className="prog-card">
                  <div className="prog-card-header">
                    <span className="prog-card-icon">🏁</span>
                    <span className="prog-card-title">Status</span>
                  </div>
                  <div className="prog-card-body">
                    <button className="prog-btn prog-btn--status" onClick={() => { /* TODO */ }} disabled>
                      <span className="prog-btn-icon">🏁</span> Flag Completed
                    </button>
                  </div>
                </div>
              </div>

              {/* Checkpoint Timeline Modal */}
              {showTimelineModal && (
                <CheckpointTimelineModal
                  draftId={id}
                  versionId={currentVersionId}
                  checkpoints={checkpoints}
                  onClose={() => setShowTimelineModal(false)}
                  onRestored={(snap) => applyCheckpointSnap(snap)}
                  onCheckpointsChanged={setCheckpoints}
                  canvas={canvas}
                  faceDims={faceDims}
                  forceSave={forceSave}
                />
              )}
            </div>
          )}
          </div>
          )}
        </div>
      )}

      {/* Main content area */}
      <div className="designer-body">
        <div className="canvas-area">
          {/* -- View tabs -- */}
          <div className="view-tabs">
            {[
              { key: 'full', label: 'Full View' },
              { key: 'front', label: 'Front Cover' },
              { key: 'back', label: 'Back Cover' },
              { key: 'spine', label: 'Spine' },
              { key: 'onshelf', label: 'On Shelf' },
              { key: '3d', label: '3D View' },
            ].map(function(item) {
              return (
                <button
                  key={item.key}
                  className={'view-tab' + (activeView === item.key ? ' active' : '')}
                  onClick={function() {
                    if (item.key === '3d') capture3dSnapshots();
                    setActiveView(item.key);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Full View: face region labels */}
          {activeView === 'full' && !readOnly && (
            <div className="surface-indicator">
              Full Wraparound View
              <span style={{ opacity: 0.6, marginLeft: 8, fontSize: '0.7rem' }}>
                Objects can span across faces. Dashed lines mark fold boundaries.
              </span>
            </div>
          )}

          {/* Single-face label */}
          {faceLabel && !readOnly && (
            <div className="surface-indicator">
              Editing: <strong>{faceLabel}</strong>
              <span style={{ opacity: 0.6, marginLeft: 8, fontSize: '0.7rem' }}>
                Objects outside this face are hidden (clipped)
              </span>
            </div>
          )}

          {/* ISBN/Blurb buttons moved to Cover tab in toolbar */}

          {/* Flex-row wrapper: zoom bar on the left, workspace/3D on the right */}
          <div className="canvas-workspace">
            {/* Zoom bar — always visible, works for both canvas and 3D */}
            <div className="zoom-bar">
              <button className="zoom-btn" onClick={zoomIn}  title="Zoom in"  disabled={zoom >= ZOOM_MAX}>+</button>
              <input
                className="zoom-input"
                type="text"
                value={zoomInputVal}
                title="Type a zoom % and press Enter. Double-click to reset to 100%"
                onChange={e => setZoomInputVal(e.target.value.replace(/[^0-9]/g, ''))}
                onFocus={() => { zoomInputFocused.current = true; }}
                onDoubleClick={zoomReset}
                onBlur={applyZoomInput}
                onKeyDown={e => {
                  if (e.key === 'Enter') { applyZoomInput(); e.target.blur(); }
                  if (e.key === 'Escape') {
                    zoomInputFocused.current = false;
                    setZoomInputVal(String(Math.round(zoom * 100)));
                    e.target.blur();
                  }
                }}
              />
              <button className="zoom-btn" onClick={zoomOut} title="Zoom out" disabled={zoom <= ZOOM_MIN}>−</button>
              <button
                className={`zoom-btn ruler-toggle-btn${showRulers ? ' ruler-toggle-btn--on' : ''}`}
                onClick={() => setShowRulers(r => !r)}
                title={showRulers ? 'Hide rulers' : 'Show rulers'}
                style={{ marginTop: 8 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="10" rx="1" />
                  <line x1="6"  y1="7" x2="6"  y2="11" />
                  <line x1="10" y1="7" x2="10" y2="13" />
                  <line x1="14" y1="7" x2="14" y2="11" />
                  <line x1="18" y1="7" x2="18" y2="11" />
                </svg>
              </button>
              {showRulers && (
                <button
                  className={`zoom-btn ruler-unit-btn${rulerUnit === 'cm' ? ' ruler-toggle-btn--on' : ''}`}
                  onClick={() => setRulerUnit(u => u === 'in' ? 'cm' : 'in')}
                  title={`Switch to ${rulerUnit === 'in' ? 'centimeters' : 'inches'}`}
                  style={{ fontSize: '0.6rem', marginTop: 4, letterSpacing: 0 }}
                >{rulerUnit === 'in' ? 'in' : 'cm'}</button>
              )}
            </div>

            {/* Canvas area: optional rulers above/left, scrollable canvas fills the rest */}
            <div className="canvas-right-side">
              {rulerVisible && (
                <div className="ruler-top-row">
                  <div className="ruler-corner" />
                  <canvas ref={hRulerRef} className="ruler-h" height={RULER_SIZE} />
                </div>
              )}
              <div className="canvas-main-row" style={{ position: 'relative' }}>
                {rulerVisible && <canvas ref={vRulerRef} className="ruler-v" width={RULER_SIZE} />}
                {/* Scrollable canvas area — always scrollable so the full cover is reachable */}
                <div
                  className="canvas-scroll-area"
                  ref={scrollRef}
                  onContextMenu={(e) => {
                    if (!canvas || canvas.isDrawingMode || placingText) return;
                    e.preventDefault();
                    const target = canvas.findTarget(e.nativeEvent);
                    if (!target) return;
                    if (target.name?.startsWith('_guide')) return;
                    if (target === canvas.backgroundImage) return;
                    if (target.type === 'activeSelection') return;
                    const collabId = target._collabId;
                    const count = collabId ? (objCommentCounts[collabId] || 0) : 0;
                    setCtxMenu({ x: e.clientX, y: e.clientY, fabricObj: target, count });
                  }}
                  style={
                    activeView === 'onshelf'
                      ? { height: spineW ? (spineW * zoom + 60) + 'px' : '160px', overflowY: 'hidden' }
                      : activeView === '3d'
                        ? { height: faceH ? (faceH + 60) + 'px' : '600px' }
                        : { height: faceH ? (faceH + (activeView === 'full' ? 80 : 60)) + 'px' : '600px' }
                  }
                >
                  <div
                    className={[
                      'canvas-display',
                      activeView === 'full' ? 'full-view-wrapper' : 'single-face-wrapper',
                      activeView === 'onshelf' ? 'onshelf-view' : '',
                    ].join(' ')}
                    style={
                      activeView === '3d' ? { display: 'none' } :
                      !canvasReady ? { visibility: 'hidden' } :
                      activeView === 'onshelf' ? { minWidth: (faceH * zoom + 60) + 'px' } :
                      undefined
                    }
                  >
                    <div className={activeView === 'onshelf' ? 'onshelf-rotate-wrapper' : undefined}>
                      <canvas ref={canvasRef} />
                    </div>
                    {ctxMenu && (
                      <CanvasContextMenu
                        x={ctxMenu.x}
                        y={ctxMenu.y}
                        fabricObj={ctxMenu.fabricObj}
                        commentCount={ctxMenu.count}
                        onComment={handleObjectCommentClick}
                        onClose={() => setCtxMenu(null)}
                      />
                    )}
                  </div>

                  {/* -- 3D View — block wrapper breaks flex:1 on book3d-container so the
                       scene's minHeight drives overflow and scrollbars appear at high zoom -- */}
                  {activeView === '3d' && (
                    <div style={{ width: '100%' }}>
                      <BookCover3D
                        ref={book3dRef}
                        frontImg={snapshots3d.front}
                        spineImg={snapshots3d.spine}
                        backImg={snapshots3d.back}
                        bookSize={versionData.bookSize}
                        paperType={versionData.paperType}
                        lamination={versionData.lamination}
                        bindingType={versionData.bindingType}
                        coverType={versionData.coverType}
                        spineW={spineW}
                        zoom={zoom}
                      />
                    </div>
                  )}
                </div>
                {/* Crosshair overlay — hidden when rulers are off */}
                {rulerVisible && (
                  <div
                    ref={crosshairRef}
                    className="ruler-crosshair"
                    style={{ left: RULER_SIZE }}
                  >
                    <div className="crosshair-h" />
                    <div className="crosshair-v" />
                  </div>
                )}
                {/* Coordinate readout tooltip */}
                {rulerVisible && (
                  <div ref={coordReadoutRef} className="ruler-coord-readout" />
                )}
              </div>
            </div>
          </div>

          {/* 3D rotate controls — outside the scroll area so they never scroll with the book */}
          {activeView === '3d' && (
            <div className="book3d-controls">
              <span className="book3d-label">↻ Drag to rotate</span>
              <div className="book3d-buttons">
                <button className="book3d-btn" title="Rotate left"  onClick={() => book3dRef.current?.rotateLeft()}>◀</button>
                <button className="book3d-btn book3d-btn-center" title="Reset view" onClick={() => book3dRef.current?.resetView()}>⊙</button>
                <button className="book3d-btn" title="Rotate right" onClick={() => book3dRef.current?.rotateRight()}>▶</button>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar: unified tabbed panel (Info / Chat / Print Specifications / Team / Activity) */}
          <CoverRightSidebar
            draftId={id}
            versionId={currentVersionId}
            isAuthor={isAuthor}
            collaboratorRole={collaboratorRole}
            versionData={versionData}
            onCriteriaUpdated={function(c) { setVersionData(function(prev) { return Object.assign({}, prev, { criteria: c }); }); }}
            canvas={canvas}
            canvasReady={!isHydrating}
            faceDims={faceDims}
            activeObjComment={activeObjComment}
            onCloseObjComment={handleCloseObjComment}
            objCommentCounts={objCommentCounts}
            onObjCountChange={handleObjCountChange}
            onHighlightObject={handleHighlightObject}
            collabSideView={collabSideView}
            setCollabSideView={setCollabSideView}
            onInvite={() => setInviteSignal(s => s + 1)}
            onRestoreCheckpoint={requestRestoreCheckpoint}
            checkpointRestoring={checkpointRestoring}
            triggerChange={triggerChange}
          />

        {/* Headless CollaboratorBar — renders only the invite popup */}
        {isAuthor && (
          <CollaboratorBar
            draftId={id}
            versionId={currentVersionId}
            collaborators={[]}
            isAuthor={isAuthor}
            onCollaboratorsChange={() => {}}
            openAddDialogSignal={inviteSignal}
            hideButton
            hideList
          />
        )}

        {/* ── Save checkpoint modal ──────────────────────────────── */}
        {showCheckpointPrompt && (
          <div className="checkpoint-save-overlay" onClick={() => { if (!checkpointSaving) { setShowCheckpointPrompt(false); setCheckpointLabel(''); } }}>
            <div className="checkpoint-save-modal" onClick={e => e.stopPropagation()}>
              <h3>📌 Save Checkpoint</h3>
              <p className="checkpoint-save-modal-info">Give this checkpoint a name so you can find it later.</p>
              <input
                className="checkpoint-save-modal-input"
                type="text"
                placeholder='e.g. "Before changing fonts"'
                value={checkpointLabel}
                onChange={e => setCheckpointLabel(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveCheckpoint(checkpointLabel);
                  if (e.key === 'Escape') { setShowCheckpointPrompt(false); setCheckpointLabel(''); }
                }}
                autoFocus
                disabled={checkpointSaving}
              />
              <p className="checkpoint-save-modal-hint">Press Enter to save, Esc to cancel. Leave blank for auto-timestamp.</p>
              <div className="checkpoint-save-modal-actions">
                <button
                  className="checkpoint-save-modal-cancel"
                  onClick={() => { setShowCheckpointPrompt(false); setCheckpointLabel(''); }}
                  disabled={checkpointSaving}
                >
                  Cancel
                </button>
                <button
                  className="checkpoint-save-modal-confirm"
                  onClick={() => handleSaveCheckpoint(checkpointLabel)}
                  disabled={checkpointSaving}
                >
                  {checkpointSaving ? '⏳ Saving...' : '📌 Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Restore confirmation modal ─────────────────────────── */}
        {restoreModal && (
          <div className="checkpoint-restore-overlay" onClick={() => setRestoreModal(null)}>
            <div className="checkpoint-restore-modal" onClick={e => e.stopPropagation()}>
              <h3>Restore checkpoint?</h3>
              <p className="checkpoint-restore-modal-label">"{restoreModal.label}"</p>
              {restoreModal.savedByName && (
                <p className="checkpoint-restore-modal-meta">
                  Saved by {restoreModal.savedByName}
                  {restoreModal.savedByRole ? ` (${restoreModal.savedByRole})` : ''}
                  {restoreModal.createdAt ? ` · ${new Date(restoreModal.createdAt).toLocaleString()}` : ''}
                </p>
              )}
              <p className="checkpoint-restore-modal-info">Your current design will be auto-saved before restoring.</p>
              <div className="checkpoint-restore-modal-actions">
                <button className="checkpoint-restore-modal-cancel" onClick={() => setRestoreModal(null)}>Cancel</button>
                <button className="checkpoint-restore-modal-confirm" onClick={confirmRestoreCheckpoint}>Restore</button>
              </div>
            </div>
          </div>
        )}

        {/* ── PDF preview modal ──────────────────────────────────── */}
        {showPdfPreview && (
          <PdfPreviewModal
            pages={pdfPages}
            pdfGenerating={pdfGenerating}
            versionName={versionData?.name}
            onClose={() => setShowPdfPreview(false)}
          />
        )}

        {/* ── Checkpoint limit reached modal ─────────────────────── */}
        {showCheckpointLimitModal && (
          <div className="checkpoint-limit-overlay" onClick={() => { setShowCheckpointLimitModal(false); setShowTimelineModal(true); }}>
            <div className="checkpoint-limit-modal" onClick={e => e.stopPropagation()}>
              <h3>📌 Checkpoint limit reached</h3>
              <p className="checkpoint-limit-modal-info">
                You have reached the max number of checkpoints allowed. Please delete an exisitng checkpoint to create a new one.
              </p>
              <div className="checkpoint-limit-modal-actions">
                <button
                  className="checkpoint-limit-modal-confirm"
                  onClick={() => { setShowCheckpointLimitModal(false); setShowTimelineModal(true); }}
                  autoFocus
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
